import { useState, useCallback, useEffect } from 'react'
import { api } from '../../api/client'
import type { Conversation, ChannelAgent } from '../../api/client'
import { useToast } from '../../utils/toast'

interface ChannelsPanelProps {
  onClose: () => void
}

export function ChannelsPanel({ onClose }: ChannelsPanelProps) {
  const { addToast } = useToast()
  const [channels, setChannels] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedChannel, setSelectedChannel] = useState<Conversation | null>(null)
  const [agents, setAgents] = useState<ChannelAgent[]>([])
  const [loadingAgents, setLoadingAgents] = useState(false)

  // Create form state
  const [creating, setCreating] = useState(false)
  const [newHandle, setNewHandle] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [newInstructions, setNewInstructions] = useState('')
  const [saving, setSaving] = useState(false)

  // Add agent form state
  const [addingAgent, setAddingAgent] = useState(false)
  const [newAgent, setNewAgent] = useState('')
  const [newDeliveryMode, setNewDeliveryMode] = useState('cron')
  const [newAlsoPostSlack, setNewAlsoPostSlack] = useState(true)
  const [savingAgent, setSavingAgent] = useState(false)

  useEffect(() => {
    api.listChannels()
      .then(list => setChannels(list))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleSelectChannel = useCallback(async (ch: Conversation) => {
    setSelectedChannel(ch)
    setLoadingAgents(true)
    try {
      const list = await api.listChannelAgents(ch.id)
      setAgents(list)
    } catch {
      setAgents([])
    } finally {
      setLoadingAgents(false)
    }
  }, [])

  const handleCreate = useCallback(async () => {
    if (!newHandle.trim() || saving) return
    setSaving(true)
    try {
      const ch = await api.createChannel({
        handle: newHandle.trim(),
        title: newTitle.trim() || undefined,
        instructions: newInstructions.trim() || undefined,
      })
      setChannels(prev => [ch, ...prev])
      setNewHandle('')
      setNewTitle('')
      setNewInstructions('')
      setCreating(false)
      addToast(`Channel #${ch.handle ?? ch.id} created.`, 'success')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Create failed.', 'error')
    } finally {
      setSaving(false)
    }
  }, [newHandle, newTitle, newInstructions, saving, addToast])

  const handleAddAgent = useCallback(async () => {
    if (!selectedChannel || !newAgent.trim() || savingAgent) return
    setSavingAgent(true)
    try {
      const ca = await api.addChannelAgent(selectedChannel.id, {
        agent: newAgent.trim(),
        delivery_mode: newDeliveryMode,
        also_post_slack: newAlsoPostSlack,
      })
      setAgents(prev => [...prev, ca])
      setNewAgent('')
      setAddingAgent(false)
      addToast(`Agent ${ca.agent} registered.`, 'success')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Add agent failed.', 'error')
    } finally {
      setSavingAgent(false)
    }
  }, [selectedChannel, newAgent, newDeliveryMode, newAlsoPostSlack, savingAgent, addToast])

  const handleToggleActive = useCallback(async (ca: ChannelAgent) => {
    if (!selectedChannel) return
    try {
      const updated = await api.updateChannelAgent(selectedChannel.id, ca.agent, { active: !ca.active })
      setAgents(prev => prev.map(a => a.agent === ca.agent ? updated : a))
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Update failed.', 'error')
    }
  }, [selectedChannel, addToast])

  const handleRemoveAgent = useCallback(async (ca: ChannelAgent) => {
    if (!selectedChannel) return
    if (!window.confirm(`Remove agent "${ca.agent}" from this channel?`)) return
    try {
      await api.removeChannelAgent(selectedChannel.id, ca.agent)
      setAgents(prev => prev.filter(a => a.agent !== ca.agent))
      addToast(`Agent ${ca.agent} removed.`, 'success')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Remove failed.', 'error')
    }
  }, [selectedChannel, addToast])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Manage Channels</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">x</button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Channel list */}
          <div className="w-56 shrink-0 border-r border-gray-200 flex flex-col">
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
              {loading && <p className="text-xs text-gray-400 text-center py-4 animate-pulse">Loading...</p>}
              {!loading && channels.length === 0 && !creating && (
                <p className="text-xs text-gray-400 text-center py-4">No channels yet.</p>
              )}
              {channels.map(ch => (
                <button
                  key={ch.id}
                  onClick={() => void handleSelectChannel(ch)}
                  className={`w-full text-left px-2.5 py-2 rounded-lg transition-colors ${
                    selectedChannel?.id === ch.id
                      ? 'bg-teal-50 border border-teal-200 text-teal-800'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <p className="text-sm font-medium truncate">#{ch.handle ?? ch.id}</p>
                  {ch.title && (
                    <p className="text-xs text-gray-400 truncate">{ch.title}</p>
                  )}
                </button>
              ))}
              {creating && (
                <div className="border border-teal-200 rounded-lg px-2.5 py-2 bg-teal-50/30 space-y-1.5">
                  <input
                    autoFocus
                    value={newHandle}
                    onChange={e => setNewHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                    placeholder="handle (e.g. general)"
                    className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400"
                    onKeyDown={e => {
                      if (e.key === 'Enter') void handleCreate()
                      if (e.key === 'Escape') setCreating(false)
                    }}
                  />
                  <input
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    placeholder="title (optional)"
                    className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400"
                  />
                  <textarea
                    value={newInstructions}
                    onChange={e => setNewInstructions(e.target.value)}
                    placeholder="instructions (optional)..."
                    rows={2}
                    className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400 resize-none"
                  />
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => void handleCreate()}
                      disabled={!newHandle.trim() || saving}
                      className="text-xs px-2 py-0.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => { setCreating(false); setNewHandle(''); setNewTitle(''); setNewInstructions('') }}
                      className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="px-3 py-2 border-t border-gray-100">
              <button
                onClick={() => setCreating(true)}
                disabled={creating}
                className="text-xs text-teal-600 hover:text-teal-800 font-medium disabled:opacity-40"
              >
                + New channel
              </button>
            </div>
          </div>

          {/* Agent registrations */}
          <div className="flex-1 flex flex-col min-h-0">
            {!selectedChannel ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-gray-400">Select a channel to manage agents.</p>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-teal-800">#{selectedChannel.handle ?? selectedChannel.id}</p>
                  {selectedChannel.title && (
                    <p className="text-xs text-gray-500">{selectedChannel.title}</p>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Agent Deliveries</p>
                  {loadingAgents && (
                    <p className="text-xs text-gray-400 animate-pulse">Loading agents...</p>
                  )}
                  {!loadingAgents && agents.length === 0 && !addingAgent && (
                    <p className="text-xs text-gray-400">No agents registered.</p>
                  )}
                  {agents.map(ca => (
                    <div key={ca.agent} className={`border rounded-lg px-3 py-2 ${ca.active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-mono font-medium text-gray-800">{ca.agent}</p>
                          <p className="text-xs text-gray-400">
                            {ca.delivery_modes.join(', ') || 'no mode'} · {ca.also_post_slack ? 'posts to Slack' : 'no Slack'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => void handleToggleActive(ca)}
                            className={`text-xs px-2 py-0.5 rounded transition-colors ${
                              ca.active
                                ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {ca.active ? 'Active' : 'Inactive'}
                          </button>
                          <button
                            onClick={() => void handleRemoveAgent(ca)}
                            className="text-xs text-gray-400 hover:text-red-600 px-1.5 py-0.5 rounded hover:bg-red-50 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {addingAgent && (
                    <div className="border border-indigo-200 rounded-lg px-3 py-2.5 bg-indigo-50/30 space-y-2">
                      <input
                        autoFocus
                        value={newAgent}
                        onChange={e => setNewAgent(e.target.value)}
                        placeholder="agent name (e.g. brain)"
                        className="w-full text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        onKeyDown={e => {
                          if (e.key === 'Enter') void handleAddAgent()
                          if (e.key === 'Escape') setAddingAgent(false)
                        }}
                      />
                      <div className="flex items-center gap-3">
                        <select
                          value={newDeliveryMode}
                          onChange={e => setNewDeliveryMode(e.target.value)}
                          className="text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        >
                          <option value="cron">cron</option>
                          <option value="event">event</option>
                          <option value="manual">manual</option>
                        </select>
                        <label className="flex items-center gap-1 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={newAlsoPostSlack}
                            onChange={e => setNewAlsoPostSlack(e.target.checked)}
                            className="rounded"
                          />
                          Post to Slack
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => void handleAddAgent()}
                          disabled={!newAgent.trim() || savingAgent}
                          className="text-xs px-2.5 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => { setAddingAgent(false); setNewAgent('') }}
                          className="text-xs px-2.5 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-4 py-2 border-t border-gray-100">
                  <button
                    onClick={() => setAddingAgent(true)}
                    disabled={addingAgent}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-40"
                  >
                    + Register agent
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
